import {useEffect, useState} from 'react';
import {Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select} from '@mui/material';
import api from '../hooks/useApi';
import {isSuperAdmin} from '../auth/permissions';

export const transferObject = (client, objectId, ownerId) =>
    client.patch(`/api/objects/${objectId}/transfer-owner/`, {partner_id: Number(ownerId)});

const ownerLabel = (owner) => {
    if (owner === null || owner === undefined) return '';
    if (typeof owner !== 'object') return String(owner);
    const name = [owner.name, owner.last_name, owner.second_name]
        .filter((part) => typeof part === 'string' && part.trim()).join(' ');
    return name || owner.email || String(owner.id);
};

export default function ObjectTransferDialog({open, onClose, onTransferred, objectId, objectName, currentOwner, user}) {
    const [owners, setOwners] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !isSuperAdmin(user)) return undefined;
        setLoading(true);
        setError('');
        setSelectedOwner('');
        api.get('/api/hotels/group/owner/get/')
            .then((response) => {
                const nextOwners = response.data?.users || response.data?.data || [];
                const currentId = currentOwner?.id ?? currentOwner;
                const ownerList = Array.isArray(nextOwners) ? nextOwners : [];
                const validOwners = ownerList.filter((owner) => (
                    Number.isInteger(Number(owner?.id)) && Number(owner.id) > 0 && owner.is_active &&
                    !owner.is_staff && !owner.is_superuser && Number(owner.id) !== Number(currentId)
                ));
                setOwners(validOwners);
                if (validOwners.length !== ownerList.length) {
                    setError('Один або кілька партнерів не мають коректного ID і недоступні для вибору.');
                }
            })
            .catch((requestError) => setError(requestError.response?.data?.detail || requestError.message || 'Не вдалося завантажити власників.'))
            .finally(() => {
                setLoading(false);
            });
    }, [open, user]);

    if (!isSuperAdmin(user)) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        const ownerId = Number(selectedOwner);
        if (!Number.isInteger(ownerId) || ownerId <= 0 || ownerId === Number(currentOwner?.id ?? currentOwner)) return;
        setSubmitting(true);
        setError('');
        try {
            await transferObject(api, objectId, ownerId);
            onTransferred?.(ownerId);
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.detail || 'Не вдалося передати об’єкт.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Передача об’єкта</DialogTitle>
                <DialogContent dividers>
                    {objectName && <Alert severity="info" sx={{mb: 2}}>Об’єкт: {objectName}</Alert>}
                    {currentOwner && <Alert severity="info" sx={{mb: 2}}>Поточний власник: {ownerLabel(currentOwner)}</Alert>}
                    <Alert severity="warning" sx={{mb: 2}}>Після передачі попередній партнер втратить доступ до об’єкта та пов’язаних бронювань.</Alert>
                    {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                    <FormControl fullWidth disabled={loading || submitting}>
                        <InputLabel id="transfer-owner-label">Новий власник</InputLabel>
                        <Select native labelId="transfer-owner-label" value={selectedOwner} label="Новий власник"
                                onChange={(event) => setSelectedOwner(String(event.target.value))}>
                            <option value="">Оберіть нового партнера</option>
                            {owners.map((owner) => <option key={owner.id} value={String(owner.id)}>{ownerLabel(owner)}{owner.email ? ` (${owner.email})` : ''}</option>)}
                        </Select>
                    </FormControl>
                    {loading && <CircularProgress size={20} sx={{mt: 2}} />}
                    {!loading && owners.length === 0 && !error && <Alert severity="warning" sx={{mt: 2}}>Немає доступних партнерів.</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>Скасувати</Button>
                    <Button type="submit" variant="contained" disabled={loading || submitting || selectedOwner === '' || !Number.isInteger(Number(selectedOwner)) || Number(selectedOwner) === Number(currentOwner?.id ?? currentOwner)}>
                        {submitting && <CircularProgress size={18} sx={{mr: 1}} />}Передати
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}